import axios, { AxiosError } from 'axios';

import { UserBasketItem, UserSessionProps } from '@/store';
import {
  ContentfulInformation,
  crossSellingDxlInformationProps,
  DxlInformation,
  DxlInformationPrice,
  Feature,
  Product,
} from '@/types/Product.types';

import { api } from './auth.services';
import { InitialState, ModalTypes } from '@/hooks/modal/types';

export type VendorRequiredFields = {
  organizationRegistrationNumber: string[];
  subDomain: string[];
  userPrincipleName: string[];
  firstName: string[];
  lastName: string[];
  addressLine1: string[];
  addressLine2: string[];
  addressCity: string[];
  addressState: string[];
  addressPostalCode: string[];
  alternateEmail: string[];
  addressPhoneNumber: string[];
  addressISO3Country: string[];
};
export type ProductOrderBasketProduct = {
  productUuid: DxlInformation['uuid'];
  qty: UserBasketItem['quantity'];
  unit: DxlInformationPrice['unit'];
  vendorRequiredFields: VendorRequiredFields;
};

export type ProductOrderBasket = Pick<UserSessionProps, 'organizationId' | 'userId'> & {
  products: ProductOrderBasketProduct[];
};

export type ProductOrderProps = {
  basket: ProductOrderBasket;
};

export type CreditVettingPayload = {
  bankStartDay: string;
  businessStartDate: string;
};

type ProductOrderResponse = {
  userEmail: string;
  needCreditVetting?: boolean;
  externalId: string;
  tpUrl?: string;
};

type RetrieveProductProps = Pick<Product, 'id' | 'dxlInformation'> & {
  contentfulInformation?: ContentfulInformation;
};

type ProductSummaryResponse = {
  item: {
    productId: string;
    price: number;
    numberOfUnits: number;
    pricingType: string;
    needsAdditionalInformation?: boolean;
  }[];
  subtotal: number;
  dueNow: number;
  recurringCharges: number;
  estimatedVAT: number;
  needsMCA?: boolean;
};

export type McaPayload = {
  country: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

type ButtonProps = {
  label: string;
  url: string;
};

export type CossSellingProductProps = {
  id: string;
  buttonText: ButtonProps;
  dxlInformation: crossSellingDxlInformationProps;
  vendor: string;
};

export const retrieveProduct = async (productId: string) => {
  console.log('Starting a request to retrieve products: ', `/product/${productId}`);
  const res = await api.get<RetrieveProductProps>(`/product/${productId}`).catch<AxiosError>(error => error);

  if (!axios.isAxiosError(res)) {
    console.log(
      `/product/${productId}`,
      ' The request was successfully completed with the following status: ',
      res.request?.status
    );
  } else {
    console.log(
      `/product/${productId}`,
      ' The request was completed unsuccessfully with the following status: ',
      res.request?.status,
      res?.code
    );
  }

  return res;
};
export const getCrossSellingProducts = async (queryParams: string) => {
  console.log('Starting a request to retrieve products: ', `/product?labels=${queryParams}`);
  const res = await api
    .get<CossSellingProductProps>(`/product?labels=${queryParams}`)
    .catch<AxiosError>(error => error);

  if (!axios.isAxiosError(res)) {
    console.log(
      `/product/${queryParams}`,
      ' The request was successfully completed with the following status: ',
      res.request?.status
    );
  } else {
    console.log(
      `/product/${queryParams}`,
      ' The request was completed unsuccessfully with the following status: ',
      res.request?.status,
      res?.code
    );
  }

  return res;
};


//mandar header tenant porting aqui
export const productOrder = async (
  productOrder: ProductOrderProps,
  csrfToken: string,
  setErrorInfo: (info: InitialState['errorInfo']) => void,
  toggleModal: (isModalOpen: InitialState['isModalOpen'], modalType?: ModalTypes) => void,
  tenantPorting?: boolean
) => {
  try {
    const res = await api.post<ProductOrderResponse>('/product-order', productOrder, {
      headers: { Authorization: csrfToken, ...(tenantPorting && { 'x-is-tenant-porting': tenantPorting }) },
    });
    return res;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      setErrorInfo({
        statusType: error.response?.data?.error,
        statusCode: error.response?.data?.code || error.response?.status,
        requestUrl: 'product-order',
      });
      toggleModal(true, 'Error');
      return error;
    }
  }
};

export const retrieveProductFeatures = async (productId: string) => {
  const res = await api.get<Feature[]>(`/product/${productId}/features`).catch<AxiosError>(error => error);
  return res;
};

export const retrieveProductsSummary = async (
  products: { id: string; price: number; numberOfUnits: number; pricingType: string }[],
  csrfToken: string
) => {
  type Item = {
    id: string;
    price: number;
    numberOfUnits: number;
    pricingType: string;
  };

  const res = await api
    .post<ProductSummaryResponse>(
      '/basket/summary',
      { products: products }, // Wrap products in an object
      {
        headers: { Authorization: csrfToken, 'Content-Type': 'application/json' },
      }
    )
    .catch<AxiosError>(error => error);

  return res;
};

export const creditVettingProductOrder = async (
  creditVettingData: CreditVettingPayload,
  csrfToken: string,
  setErrorInfo: (info: InitialState['errorInfo']) => void,
  toggleModal: (isModalOpen: InitialState['isModalOpen'], modalType?: ModalTypes) => void,
  shouldToggleModal: boolean = true
) => {
  try {
    const res = await api.post<{ externalId: string; userEmail: string }>(
      '/credit-vetting-product-order',
      creditVettingData,
      {
        headers: { Authorization: csrfToken },
      }
    );
    return res;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (shouldToggleModal) {
        setErrorInfo({
          statusType: error.response?.data?.error,
          statusCode: error.response?.data?.code || error.response?.status,
          requestUrl: 'product-order',
        });
        toggleModal(true, 'Error');
      } else {
        return error;
      }
    }

    return false;
  }
};

export const postMca = async (payload: McaPayload, csrfToken: string) => {
  try {
    const res = await api.post<{ url: string }>('/mca', payload, {
      headers: { Authorization: csrfToken, 'Content-Type': 'application/json' },
    });
    return res;
  } catch (error) {
    return error;
  }
};

export const getMca = async (csrfToken: string, needMca: boolean) => {
  try {
    const res = await api.get<{ mcaStatus: string }>('/mca', {
      headers: {
        Authorization: csrfToken,
        needMCA: needMca,
      },
    });
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return error.response?.data;
    }
    return error;
  }
};
